import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Eye, EyeOff, Users, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminShell } from "./AdminShell";
import { AInput, ATextarea, ASelect, AToggle, ImageInput } from "@/components/admin/AdminInputs";

const EMPTY = { title: "", location: "", strategy: "Buy, Refurbish, Refinance", price_guide: "", projected_return: "", summary: "", description: "", image: "", status: "Available", published: true, order: 0 };

function Editor({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.title) return toast.error("Title is required");
    setBusy(true);
    try {
      const body = { ...f, order: Number(f.order) || 0 };
      if (initial?.slug) await api.put(`/opportunities/${initial.slug}`, body);
      else await api.post("/opportunities", body);
      toast.success("Opportunity saved");
      onSaved();
    } catch (e) { toast.error("Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0F2E23] rounded-xl border border-brass/30 p-6 mb-6" data-testid="opportunity-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-serif text-white">{initial?.slug ? "Edit opportunity" : "New opportunity"}</h3>
        <button onClick={onClose} className="text-emerald-100/60 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AInput label="Title" value={f.title} onChange={set("title")} testId="opp-title" />
        <AInput label="Location" value={f.location} onChange={set("location")} testId="opp-location" />
        <ASelect label="Strategy" value={f.strategy} onChange={set("strategy")} testId="opp-strategy" options={["Buy, Refurbish, Refinance", "Buy, Refurbish, Sell", "Long-term Rental", "Development", "Below-market Purchase", "Value-add"]} />
        <ASelect label="Status" value={f.status} onChange={set("status")} testId="opp-status" options={["Available", "Coming Soon", "Under Offer", "Sold"]} />
        <AInput label="Price guide" value={f.price_guide} onChange={set("price_guide")} placeholder="Guide £95,000" />
        <AInput label="Projected return (illustrative)" value={f.projected_return} onChange={set("projected_return")} placeholder="Assumption only" />
      </div>
      <div className="mt-4"><ImageInput label="Image" value={f.image} onChange={set("image")} testId="opp-image-upload" /></div>
      <div className="mt-4"><ATextarea label="Summary (card text)" value={f.summary} onChange={set("summary")} testId="opp-summary" /></div>
      <div className="mt-4"><ATextarea label="Full description" value={f.description} onChange={set("description")} rows={5} /></div>
      <div className="flex items-center gap-4 mt-4">
        <div className="w-28"><AInput label="Order" type="number" value={f.order} onChange={set("order")} /></div>
        <div className="pt-5"><AToggle label="Published" value={f.published} onChange={set("published")} testId="opp-published" /></div>
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={save} disabled={busy} data-testid="opp-save" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-6 py-2 text-sm"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save opportunity"}</button>
        <button onClick={onClose} className="text-emerald-100/70 hover:text-white text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function AdminOpportunities() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [enq, setEnq] = useState({});

  const load = async () => { const { data } = await api.get("/admin/opportunities"); setItems(data); };
  useEffect(() => { load(); }, []);

  const toggleEnq = async (o) => {
    const cur = enq[o.slug];
    if (cur?.open) { setEnq((e) => ({ ...e, [o.slug]: { ...cur, open: false } })); return; }
    try {
      const { data } = await api.get("/leads", { params: { lead_type: "Investor", opportunity: o.title } });
      setEnq((e) => ({ ...e, [o.slug]: { open: true, leads: data } }));
    } catch (err) { toast.error("Could not load enquiries"); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this opportunity?")) return;
    try { await api.delete(`/opportunities/${slug}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <AdminShell title="Opportunities">
      {!editing && <button onClick={() => setEditing("new")} data-testid="opportunity-new" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-5 py-2 text-sm mb-6"><Plus className="w-4 h-4" /> New opportunity</button>}
      {editing && <Editor initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-opportunities-list">
        {items.map((o) => (
          <div key={o.slug} className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-hidden">
            <div className="h-32 bg-[#091E16]">{o.image && <img src={o.image} alt={o.title} className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-serif text-base">{o.title}</h3>
                {o.published ? <Eye className="w-3.5 h-3.5 text-emerald-300" /> : <EyeOff className="w-3.5 h-3.5 text-amber-300" />}
              </div>
              <p className="text-xs text-emerald-100/50 mt-0.5">{o.location} · {o.status}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => setEditing(o)} data-testid={`opportunity-edit-${o.slug}`} className="flex items-center gap-1 text-xs bg-white/5 hover:bg-white/10 text-white rounded-lg px-3 py-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => remove(o.slug)} data-testid={`opportunity-delete-${o.slug}`} className="flex items-center gap-1 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-300 rounded-lg px-3 py-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                <button onClick={() => toggleEnq(o)} data-testid={`opportunity-enquiries-${o.slug}`} className="flex items-center gap-1 text-xs bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 rounded-lg px-3 py-1.5"><Users className="w-3.5 h-3.5" /> Enquiries</button>
              </div>
              {enq[o.slug]?.open && (
                <div className="mt-3 border-t border-white/10 pt-3 space-y-2" data-testid={`opportunity-enquiry-list-${o.slug}`}>
                  {(enq[o.slug].leads || []).length === 0 ? (
                    <p className="text-xs text-emerald-100/50">No enquiries for this opportunity yet. When investors register interest, their contact details appear here (and you're emailed at hello@emeraldcrown.co.uk).</p>
                  ) : enq[o.slug].leads.map((l) => (
                    <div key={l.lead_id} className="text-xs bg-[#091E16] rounded-lg p-2.5">
                      <div className="text-white font-medium">{l.full_name} <span className="text-emerald-100/40">· {new Date(l.created_at).toLocaleDateString("en-GB")}</span></div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-emerald-100/70">
                        <a href={`mailto:${l.email}`} className="hover:text-brass flex items-center gap-1"><Mail className="w-3 h-3" />{l.email}</a>
                        <a href={`tel:${l.phone}`} className="hover:text-brass flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone}</a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
